import React, { useEffect, useRef, useState } from 'react';
import { FiAlertTriangle, FiEdit2, FiEye, FiPaperclip, FiPlus, FiSave, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import useEntity from '../hooks/useEntity';
import Modal from '../components/ui/Modal';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { puede } from '../utils/permisos';

const VACIA = { titulo: '', descripcion: '', urgencia: 'media', estado: 'pendiente', asignadoA: '' };
const ESTADOS = {
  pendiente: 'Pendiente',
  denegada: 'Denegada',
  en_curso: 'En curso',
  terminada: 'Terminada',
  reclamada: 'Reclamada',
};
const BADGES = {
  pendiente: 'badge-amber',
  denegada: 'badge-red',
  en_curso: 'badge-blue',
  terminada: 'badge-green',
  reclamada: 'badge-purple',
};
const URGENCIAS = {
  baja: 'Baja',
  media: 'Media',
  alta: 'Alta',
  critica: 'Crítica',
};

const nombreUsuario = (usuario) => {
  if (!usuario || typeof usuario !== 'object') return 'Usuario desconocido';
  return `${usuario.nombre || ''} ${usuario.apellidos || ''}`.trim() || usuario.email || 'Usuario desconocido';
};

const nombreAsignado = (usuario) => {
  if (usuario == null || usuario === '') return 'Sin asignar';
  if (typeof usuario !== 'object') return 'Usuario eliminado o desconocido';
  return `${usuario.nombre || ''} ${usuario.apellidos || ''}`.trim()
    || usuario.email
    || 'Usuario eliminado o desconocido';
};

const fechaHora = (fecha) => (
  fecha ? new Date(fecha).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : 'Fecha desconocida'
);

const datosFormulario = (incidencia) => ({
  titulo: incidencia?.titulo || '',
  descripcion: incidencia?.descripcion || '',
  urgencia: incidencia?.urgencia || 'media',
  estado: incidencia?.estado || 'pendiente',
  asignadoA: incidencia?.asignadoA?._id || incidencia?.asignadoA || '',
});

const normalizarBusqueda = (valor) => String(valor || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const fechaLocalISO = (fecha) => {
  if (!fecha) return '';
  const valor = new Date(fecha);
  if (Number.isNaN(valor.getTime())) return '';
  const año = valor.getFullYear();
  const mes = String(valor.getMonth() + 1).padStart(2, '0');
  const dia = String(valor.getDate()).padStart(2, '0');
  return `${año}-${mes}-${dia}`;
};

export default function Incidencias() {
  const { usuario, empresaActivaId } = useAuth();
  const { items, cargando, error, crear, actualizar, eliminar, cargar } = useEntity('incidencias');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIA);
  const [archivos, setArchivos] = useState([]);
  const [comentario, setComentario] = useState('');
  const [archivosComentario, setArchivosComentario] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [errorAccion, setErrorAccion] = useState('');
  const [editandoComentarioId, setEditandoComentarioId] = useState(null);
  const [textoComentario, setTextoComentario] = useState('');
  const [descargandoArchivoId, setDescargandoArchivoId] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroUrgencia, setFiltroUrgencia] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroFecha, setFiltroFecha] = useState('');
  const [trabajadores, setTrabajadores] = useState([]);
  const [errorTrabajadores, setErrorTrabajadores] = useState('');
  const fileRef = useRef(null);
  const commentFileRef = useRef(null);

  const puedeCrear = puede(usuario, 'incidencias', 'crear');
  const puedeEditar = puede(usuario, 'incidencias', 'editar');
  const puedeEliminar = puede(usuario, 'incidencias', 'eliminar');
  const esAdmin = usuario?.rol === 'admin' || usuario?.rol === 'superadmin';
  const miUsuarioId = String(usuario?._id || usuario?.id || '');

  const mensajeError = (err, defecto) => err?.response?.data?.error || defecto;
  const set = (campo) => (e) => setForm((actual) => ({ ...actual, [campo]: e.target.value }));

  useEffect(() => {
    let cancelado = false;
    if (!puedeEditar) {
      setTrabajadores([]);
      setErrorTrabajadores('');
      return undefined;
    }
    const cargarTrabajadores = async () => {
      setErrorTrabajadores('');
      try {
        const params = usuario?.rol === 'superadmin' && empresaActivaId ? { empresaId: empresaActivaId } : {};
        const { data } = await api.get('/incidencias/asignables', { params });
        if (!cancelado) {
          setTrabajadores((Array.isArray(data) ? data : []).filter((item) => (
            item.rol !== 'superadmin' && item.activo !== false
          )));
        }
      } catch (err) {
        if (!cancelado) {
          setTrabajadores([]);
          setErrorTrabajadores(err?.response?.data?.error || 'No se pudieron cargar los trabajadores.');
        }
      }
    };
    cargarTrabajadores();
    return () => { cancelado = true; };
  }, [puedeEditar, usuario?.rol, empresaActivaId]);

  const limpiarAdjuntos = () => {
    setArchivos([]);
    setArchivosComentario([]);
    if (fileRef.current) fileRef.current.value = '';
    if (commentFileRef.current) commentFileRef.current.value = '';
  };

  const abrirNueva = () => {
    setEditando(null);
    setForm(VACIA);
    setComentario('');
    setEditandoComentarioId(null);
    setErrorAccion('');
    limpiarAdjuntos();
    setModal(true);
  };

  const abrirDetalle = async (incidencia) => {
    setEditando(incidencia);
    setForm(datosFormulario(incidencia));
    setComentario('');
    setEditandoComentarioId(null);
    setErrorAccion('');
    limpiarAdjuntos();
    setModal(true);
    try {
      const { data } = await api.get(`/incidencias/${incidencia._id}`);
      setEditando(data);
      setForm(datosFormulario(data));
    } catch (err) {
      setErrorAccion(mensajeError(err, 'No se pudo cargar el detalle de la incidencia.'));
    }
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setErrorAccion('');
    try {
      let data;
      const asignadoA = puedeEditar ? (form.asignadoA || null) : null;
      if (archivos.length) {
        const body = new FormData();
        body.append('titulo', form.titulo);
        body.append('descripcion', form.descripcion || '');
        body.append('urgencia', form.urgencia);
        body.append('estado', form.estado);
        body.append('asignadoA', asignadoA || '');
        if (usuario?.rol === 'superadmin' && empresaActivaId) body.append('empresaId', empresaActivaId);
        archivos.forEach((archivo) => body.append('archivos', archivo));
        const respuesta = editando
          ? await api.put(`/incidencias/${editando._id}`, body, { headers: { 'Content-Type': 'multipart/form-data' } })
          : await api.post('/incidencias', body, { headers: { 'Content-Type': 'multipart/form-data' } });
        data = respuesta.data;
      } else if (editando) {
        data = await actualizar(editando._id, { ...form, asignadoA });
      } else {
        data = await crear({ ...form, asignadoA });
      }

      limpiarAdjuntos();
      await cargar();
      if (editando) {
        setEditando(data);
        setForm(datosFormulario(data));
      } else {
        setModal(false);
      }
    } catch (err) {
      setErrorAccion(mensajeError(err, 'No se pudo guardar la incidencia.'));
    } finally {
      setGuardando(false);
    }
  };

  const cambiarEstado = async (incidencia, estado) => {
    setErrorAccion('');
    try {
      const data = await actualizar(incidencia._id, { estado });
      if (editando?._id === incidencia._id) {
        setEditando(data);
        setForm((actual) => ({ ...actual, estado }));
      }
    } catch (err) {
      setErrorAccion(mensajeError(err, 'No se pudo cambiar el estado.'));
      cargar();
    }
  };

  const borrarIncidencia = async (incidencia) => {
    if (!window.confirm(`¿Eliminar la incidencia "${incidencia.titulo}" y todos sus adjuntos?`)) return;
    setErrorAccion('');
    try {
      await eliminar(incidencia._id);
      if (editando?._id === incidencia._id) setModal(false);
    } catch (err) {
      setErrorAccion(mensajeError(err, 'No se pudo eliminar la incidencia.'));
    }
  };

  const enviarComentario = async (e) => {
    e.preventDefault();
    if (!comentario.trim() && !archivosComentario.length) return;
    setEnviandoComentario(true);
    setErrorAccion('');
    try {
      let respuesta;
      if (archivosComentario.length) {
        const body = new FormData();
        body.append('texto', comentario);
        archivosComentario.forEach((archivo) => body.append('archivos', archivo));
        respuesta = await api.post(`/incidencias/${editando._id}/comentarios`, body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        respuesta = await api.post(`/incidencias/${editando._id}/comentarios`, { texto: comentario });
      }
      setEditando(respuesta.data);
      setComentario('');
      setArchivosComentario([]);
      if (commentFileRef.current) commentFileRef.current.value = '';
      cargar();
    } catch (err) {
      setErrorAccion(mensajeError(err, 'No se pudo añadir el comentario.'));
    } finally {
      setEnviandoComentario(false);
    }
  };

  const guardarComentario = async (comentarioId) => {
    if (!textoComentario.trim()) return;
    setErrorAccion('');
    try {
      const { data } = await api.put(`/incidencias/${editando._id}/comentarios/${comentarioId}`, { texto: textoComentario });
      setEditando(data);
      setEditandoComentarioId(null);
      setTextoComentario('');
      cargar();
    } catch (err) {
      setErrorAccion(mensajeError(err, 'No se pudo editar el comentario.'));
    }
  };

  const borrarComentario = async (comentarioId) => {
    if (!window.confirm('¿Eliminar este comentario y sus adjuntos?')) return;
    setErrorAccion('');
    try {
      const { data } = await api.delete(`/incidencias/${editando._id}/comentarios/${comentarioId}`);
      setEditando(data);
      cargar();
    } catch (err) {
      setErrorAccion(mensajeError(err, 'No se pudo eliminar el comentario.'));
    }
  };

  const borrarArchivo = async (archivoId) => {
    if (!window.confirm('¿Eliminar este archivo adjunto?')) return;
    setErrorAccion('');
    try {
      const { data } = await api.delete(`/incidencias/${editando._id}/archivos/${archivoId}`);
      setEditando(data);
      cargar();
    } catch (err) {
      setErrorAccion(mensajeError(err, 'No se pudo eliminar el archivo.'));
    }
  };

  const descargarArchivo = async (archivo) => {
    setErrorAccion('');
    setDescargandoArchivoId(archivo._id);
    try {
      const { data } = await api.get(`/incidencias/${editando._id}/archivos/${archivo._id}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = archivo.nombre || 'archivo';
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setErrorAccion(mensajeError(err, 'No se pudo descargar el archivo adjunto.'));
    } finally {
      setDescargandoArchivoId(null);
    }
  };

  const puedeGestionarComentario = (item) => (
    esAdmin || String(item.autor?._id || item.autor || '') === miUsuarioId
  );

  const puedeBorrarAdjunto = (autor) => (
    esAdmin || String(autor?._id || autor || '') === miUsuarioId
  );

  const historico = [...(editando?.historicoEstados || [])]
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  const primerCambioEsAlta = historico.length > 0 && !historico[0].estadoAnterior;
  const estadoInicial = historico[0]?.estadoAnterior || historico[0]?.estadoNuevo || editando?.estado || null;
  const cambiosPosteriores = primerCambioEsAlta ? historico.slice(1) : historico;
  const fechaInicial = primerCambioEsAlta ? historico[0].fecha : editando?.createdAt;
  const autorInicial = primerCambioEsAlta ? historico[0].cambiadoPor : editando?.creadoPor;
  const historicoAsignaciones = [...(editando?.historicoAsignaciones || [])]
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  const textoBusqueda = normalizarBusqueda(busqueda);
  const incidenciasFiltradas = items.filter((incidencia) => {
    const contenido = normalizarBusqueda([
      incidencia.titulo,
      incidencia.descripcion,
      URGENCIAS[incidencia.urgencia] || incidencia.urgencia,
      ESTADOS[incidencia.estado] || incidencia.estado,
      nombreAsignado(incidencia.asignadoA),
    ].join(' '));
    return (!textoBusqueda || contenido.includes(textoBusqueda))
      && (!filtroUrgencia || incidencia.urgencia === filtroUrgencia)
      && (!filtroEstado || incidencia.estado === filtroEstado)
      && (!filtroFecha || fechaLocalISO(incidencia.createdAt) === filtroFecha);
  });
  const hayFiltros = Boolean(busqueda || filtroUrgencia || filtroEstado || filtroFecha);
  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroUrgencia('');
    setFiltroEstado('');
    setFiltroFecha('');
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Incidencias</h1>
          <p className="page-subtitle">{items.length} incidencia(s) · Abre una fila para consultar su actividad</p>
        </div>
        {puedeCrear && <button type="button" className="btn btn-primary" onClick={abrirNueva}><FiPlus /> Nueva incidencia</button>}
      </div>

      {error && <div className="alert-error" role="alert">{error}</div>}
      {errorAccion && !modal && <div className="alert-error" role="alert">{errorAccion}</div>}
      {errorTrabajadores && <div className="alert-error" role="alert">{errorTrabajadores}</div>}

      <div className="table-wrap" style={{ padding: 16, marginBottom: 16 }}>
        <div className="form-grid">
          <label className="field span-2">
            <span><FiSearch style={{ marginRight: 6 }} />Buscar en incidencias</span>
            <input
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por título o descripción"
            />
          </label>
          <label className="field">
            <span>Urgencia</span>
            <select value={filtroUrgencia} onChange={(e) => setFiltroUrgencia(e.target.value)}>
              <option value="">Todas</option>
              {Object.entries(URGENCIAS).map(([valor, etiqueta]) => <option key={valor} value={valor}>{etiqueta}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Estado</span>
            <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
              <option value="">Todos</option>
              {Object.entries(ESTADOS).map(([valor, etiqueta]) => <option key={valor} value={valor}>{etiqueta}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Fecha de creación</span>
            <input type="date" value={filtroFecha} onChange={(e) => setFiltroFecha(e.target.value)} />
          </label>
          <div className="form-actions" style={{ alignItems: 'flex-end' }}>
            <span className="muted">{incidenciasFiltradas.length} de {items.length} incidencia(s)</span>
            <button type="button" className="btn" onClick={limpiarFiltros} disabled={!hayFiltros}>Limpiar filtros</button>
          </div>
        </div>
      </div>

      {cargando ? (
        <p className="empty">Cargando…</p>
      ) : items.length === 0 ? (
        <p className="empty"><FiAlertTriangle /> No hay incidencias.</p>
      ) : incidenciasFiltradas.length === 0 ? (
        <p className="empty"><FiSearch /> No hay incidencias que coincidan con los filtros.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Incidencia</th><th>Urgencia</th><th>Estado</th><th>Asignado a</th><th>Creada por</th><th>Fecha</th><th></th></tr></thead>
            <tbody>
              {incidenciasFiltradas.map((incidencia) => (
                <tr key={incidencia._id} onDoubleClick={() => abrirDetalle(incidencia)} style={{ cursor: 'pointer' }}>
                  <td data-label="Incidencia">
                    <strong>{incidencia.titulo}</strong>
                    {(incidencia.comentarios?.length > 0 || incidencia.archivos?.length > 0) && (
                      <span className="sub" style={{ marginLeft: 8 }}>
                        {incidencia.comentarios?.length ? `💬 ${incidencia.comentarios.length} ` : ''}
                        {incidencia.archivos?.length ? `📎 ${incidencia.archivos.length}` : ''}
                      </span>
                    )}
                  </td>
                  <td data-label="Urgencia">{URGENCIAS[incidencia.urgencia] || incidencia.urgencia || '—'}</td>
                  <td data-label="Estado">
                    {puedeEditar ? (
                      <select className="mini-select" value={incidencia.estado} onClick={(e) => e.stopPropagation()} onChange={(e) => cambiarEstado(incidencia, e.target.value)} aria-label={`Estado de ${incidencia.titulo}`}>
                        {Object.entries(ESTADOS).map(([valor, etiqueta]) => <option key={valor} value={valor}>{etiqueta}</option>)}
                      </select>
                    ) : <span className={`badge ${BADGES[incidencia.estado] || ''}`}>{ESTADOS[incidencia.estado] || incidencia.estado}</span>}
                  </td>
                  <td data-label="Asignado a">{nombreAsignado(incidencia.asignadoA)}</td>
                  <td data-label="Creada por">{nombreUsuario(incidencia.creadoPor)}</td>
                  <td data-label="Fecha">{fechaHora(incidencia.createdAt)}</td>
                  <td className="acciones" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="icon-btn" onClick={() => abrirDetalle(incidencia)} title="Ver incidencia" aria-label={`Ver ${incidencia.titulo}`}><FiEye /></button>
                    {puedeEliminar && <button type="button" className="icon-btn danger" onClick={() => borrarIncidencia(incidencia)} title="Eliminar incidencia" aria-label={`Eliminar ${incidencia.titulo}`}><FiTrash2 /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo={editando ? `Incidencia: ${editando.titulo}` : 'Nueva incidencia'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {errorAccion && <div className="alert-error" role="alert">{errorAccion}</div>}

          <form onSubmit={guardar} className="form-grid">
            <h4 className="span-2 permisos-titulo" style={{ marginTop: 0 }}>{editando ? 'Datos de la incidencia' : 'Datos de la nueva incidencia'}</h4>
            <label className="field span-2">
              <span>Título *</span>
              <input required maxLength={200} value={form.titulo} onChange={set('titulo')} disabled={!!editando && !puedeEditar} />
            </label>
            <label className="field span-2">
              <span>Descripción</span>
              <textarea rows={4} value={form.descripcion || ''} onChange={set('descripcion')} disabled={!!editando && !puedeEditar} />
            </label>
            <label className="field">
              <span>Urgencia</span>
              <select value={form.urgencia} onChange={set('urgencia')} disabled={!!editando && !puedeEditar}>
                {Object.entries(URGENCIAS).map(([valor, etiqueta]) => <option key={valor} value={valor}>{etiqueta}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Estado</span>
              <select value={form.estado} onChange={set('estado')} disabled={!editando || !puedeEditar}>
                {Object.entries(ESTADOS).map(([valor, etiqueta]) => <option key={valor} value={valor}>{etiqueta}</option>)}
              </select>
            </label>
            {puedeEditar && (
              <label className="field span-2">
                <span>Asignado a</span>
                <select
                  value={form.asignadoA || ''}
                  onChange={set('asignadoA')}
                  disabled={Boolean(errorTrabajadores)}
                >
                  <option value="">Sin asignar</option>
                  {trabajadores.map((trabajador) => (
                    <option key={trabajador._id} value={trabajador._id}>{nombreUsuario(trabajador)}</option>
                  ))}
                </select>
                {errorTrabajadores && <span className="muted">No se puede cambiar la asignación hasta cargar los trabajadores.</span>}
              </label>
            )}
            {(!editando || puedeEditar) && (
              <label className="field span-2">
                <span>{editando ? 'Añadir imágenes o adjuntos' : 'Imágenes y archivos adjuntos'}</span>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.pdf" multiple onChange={(e) => setArchivos(Array.from(e.target.files || []))} />
              </label>
            )}
            {(!editando || puedeEditar) && (
              <div className="form-actions span-2">
                <button type="submit" className="btn btn-primary" disabled={guardando}>
                  {guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Crear incidencia'}
                </button>
              </div>
            )}
          </form>

          {editando && (
            <section>
              <h4 className="permisos-titulo">Adjuntos ({editando.archivos?.length || 0})</h4>
              {!editando.archivos?.length ? <p className="muted">Sin archivos adjuntos.</p> : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
                  {editando.archivos.map((archivo) => (
                    <li key={archivo._id} style={{ padding: 10, border: '1px solid var(--border)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <button type="button" className="btn btn-link" onClick={() => descargarArchivo(archivo)} disabled={descargandoArchivoId === archivo._id}>
                        <FiPaperclip /> {descargandoArchivoId === archivo._id ? 'Descargando…' : archivo.nombre}
                      </button>
                      {puedeBorrarAdjunto(editando.creadoPor) && <button type="button" className="icon-btn danger" onClick={() => borrarArchivo(archivo._id)} title="Eliminar archivo"><FiTrash2 /></button>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {editando && (
            <section>
              <h4 className="permisos-titulo">Histórico de estados</h4>
              <div className="table-wrap" style={{ padding: 14 }}>
                {estadoInicial && (
                  <p style={{ margin: historico.length ? '0 0 12px' : 0 }}>
                    <strong>Estado inicial:</strong> <span className={`badge ${BADGES[estadoInicial] || ''}`}>{ESTADOS[estadoInicial] || estadoInicial}</span>
                    {fechaInicial ? ` · ${fechaHora(fechaInicial)}` : ''}
                    {autorInicial ? ` · ${nombreUsuario(autorInicial)}` : ''}
                  </p>
                )}
                {!cambiosPosteriores.length ? <p className="muted" style={{ margin: 0 }}>Todavía no hay cambios de estado.</p> : (
                  <ol style={{ margin: 0, paddingLeft: 22, display: 'grid', gap: 10 }}>
                    {cambiosPosteriores.map((cambio, indice) => (
                      <li key={cambio._id || `${cambio.fecha}-${indice}`}>
                        <strong>{ESTADOS[cambio.estadoAnterior] || cambio.estadoAnterior || 'Sin estado'}</strong>
                        {' → '}
                        <strong>{ESTADOS[cambio.estadoNuevo] || cambio.estadoNuevo}</strong>
                        <div className="muted">{fechaHora(cambio.fecha)} · {nombreUsuario(cambio.cambiadoPor)}</div>
                        {cambio.motivo ? <div>{cambio.motivo}</div> : null}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </section>
          )}

          {editando && (
            <section>
              <h4 className="permisos-titulo">Histórico de asignaciones</h4>
              <div className="table-wrap" style={{ padding: 14 }}>
                <p style={{ margin: historicoAsignaciones.length ? '0 0 12px' : 0 }}>
                  <strong>Asignado actual:</strong> {nombreAsignado(editando.asignadoA)}
                </p>
                {!historicoAsignaciones.length ? (
                  <p className="muted" style={{ margin: 0 }}>Todavía no hay cambios de asignación.</p>
                ) : (
                  <ol style={{ margin: 0, paddingLeft: 22, display: 'grid', gap: 10 }}>
                    {historicoAsignaciones.map((cambio, indice) => (
                      <li key={cambio._id || `${cambio.fecha}-${indice}`}>
                        <strong>{nombreAsignado(cambio.asignadoAnterior)}</strong>
                        {' → '}
                        <strong>{nombreAsignado(cambio.asignadoNuevo)}</strong>
                        <div className="muted">
                          {fechaHora(cambio.fecha)} · {nombreUsuario(cambio.cambiadoPor)}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </section>
          )}

          {editando && (
            <section>
              <h4 className="permisos-titulo">Comentarios ({editando.comentarios?.length || 0})</h4>
              <div style={{ display: 'grid', gap: 10, maxHeight: 340, overflowY: 'auto', marginBottom: 14 }}>
                {!editando.comentarios?.length && <p className="muted">No hay comentarios todavía.</p>}
                {(editando.comentarios || []).map((item) => {
                  const gestionable = puedeGestionarComentario(item);
                  const enEdicion = editandoComentarioId === item._id;
                  return (
                    <article key={item._id} style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <div><strong>{nombreUsuario(item.autor)}</strong> <span className="muted">{fechaHora(item.createdAt || item.fecha)}</span></div>
                        {gestionable && !enEdicion && (
                          <div>
                            <button type="button" className="icon-btn" onClick={() => { setEditandoComentarioId(item._id); setTextoComentario(item.texto || ''); }} title="Editar comentario"><FiEdit2 /></button>
                            <button type="button" className="icon-btn danger" onClick={() => borrarComentario(item._id)} title="Eliminar comentario"><FiTrash2 /></button>
                          </div>
                        )}
                      </div>
                      {enEdicion ? (
                        <div className="field" style={{ marginTop: 8 }}>
                          <textarea rows={2} value={textoComentario} onChange={(e) => setTextoComentario(e.target.value)} />
                          <div className="form-actions">
                            <button type="button" className="btn btn-sm" onClick={() => setEditandoComentarioId(null)}><FiX /> Cancelar</button>
                            <button type="button" className="btn btn-primary btn-sm" onClick={() => guardarComentario(item._id)}><FiSave /> Guardar</button>
                          </div>
                        </div>
                      ) : item.texto ? <p style={{ whiteSpace: 'pre-wrap' }}>{item.texto}</p> : null}
                      {(item.archivos || []).map((archivo) => (
                        <div key={archivo._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 6 }}>
                          <button type="button" className="btn btn-link" onClick={() => descargarArchivo(archivo)} disabled={descargandoArchivoId === archivo._id}>
                            <FiPaperclip /> {descargandoArchivoId === archivo._id ? 'Descargando…' : archivo.nombre}
                          </button>
                          {gestionable && <button type="button" className="icon-btn danger" onClick={() => borrarArchivo(archivo._id)} title="Eliminar archivo"><FiTrash2 /></button>}
                        </div>
                      ))}
                    </article>
                  );
                })}
              </div>
              <form onSubmit={enviarComentario} className="form-grid" style={{ padding: 12, background: '#f8fafc', borderRadius: 8 }}>
                <label className="field span-2">
                  <span>Añadir comentario</span>
                  <textarea rows={2} value={comentario} onChange={(e) => setComentario(e.target.value)} placeholder="Escribe un comentario…" />
                </label>
                <label className="field span-2">
                  <span>Adjuntar imágenes o archivos</span>
                  <input ref={commentFileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.pdf" multiple onChange={(e) => setArchivosComentario(Array.from(e.target.files || []))} />
                </label>
                <div className="form-actions span-2">
                  <button type="submit" className="btn btn-primary" disabled={enviandoComentario || (!comentario.trim() && !archivosComentario.length)}>
                    {enviandoComentario ? 'Enviando…' : 'Comentar'}
                  </button>
                </div>
              </form>
            </section>
          )}
        </div>
      </Modal>
    </div>
  );
}
